import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { tickStore, type TickLike } from '../renderer/tick-store.js';
import { evaluateStaleness } from '../state/staleness.js';
import { getSymbolLabel } from '../state/symbol-labels.js';
import { formatPrice, formatDelta } from '../state/format.js';
import { connectionState } from '../state/connection-state.js';

/**
 * Tek sembol hücresi — Fiyat Tablosu (grid) kartı. Light DOM (cell.css erişir).
 *
 * Kuyumcu/döviz panosu düzeni: isim → ALIŞ | SATIŞ etiketli iki sütun (her ikisi
 * eşit vurgu). Fiyat değişiminde kısa flash (dikkat çeker) + yön oku. Yazı boyutu
 * cell.css'te container-query ile hücreyi doldurur (sabit px değil).
 */
@customElement('fp-symbol-cell')
export class FpSymbolCell extends LitElement {
    @property({ type: String }) symbol = '';
    @property({ type: Array }) show: string[] = ['bid', 'ask', 'direction'];
    /** Ürün adı görünümü: 'plain' (düz metin) | 'band' (üstte dolu şerit). */
    @property({ type: String }) nameStyle: 'plain' | 'band' = 'plain';

    @state() private tick?: TickLike;
    @state() private direction: 'up' | 'down' | 'flat' = 'flat';
    @state() private flash: '' | 'up' | 'down' = '';

    private prevBid?: string;
    private unsubscribe?: () => void;
    private stalenessTimer?: number;
    private flashTimer?: number;
    private lastEpoch = connectionState.reconnectEpoch;

    createRenderRoot(): HTMLElement {
        return this;
    }

    connectedCallback(): void {
        super.connectedCallback();
        const existing = tickStore.get(this.symbol);
        if (existing) {
            this.prevBid = existing.bid;
            this.tick = existing;
        }
        this.unsubscribe = tickStore.subscribe((sym, tick) => {
            if (sym !== this.symbol) return;
            this.computeDirection(tick.bid);
            this.tick = tick;
            this.applyStaleness();
        });
        // Tick gelmese bile yaş ilerler — 1sn'de bir staleness yeniden değerlendir
        this.stalenessTimer = window.setInterval(() => this.applyStaleness(), 1000);
        this.applyStaleness();
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.unsubscribe?.();
        this.unsubscribe = undefined;
        if (this.stalenessTimer !== undefined) {
            clearInterval(this.stalenessTimer);
            this.stalenessTimer = undefined;
        }
        if (this.flashTimer !== undefined) {
            clearTimeout(this.flashTimer);
            this.flashTimer = undefined;
        }
    }

    /** Host element'e staleness-* sınıfı + data-stale-label enjekte eder. */
    private applyStaleness(): void {
        const result = evaluateStaleness(this.tick?.server_ts);
        this.classList.remove('staleness-dim', 'staleness-warn');
        if (result.level === 'dim') {
            this.classList.add('staleness-dim');
        } else if (result.level === 'warn' || result.level === 'critical') {
            this.classList.add('staleness-warn');
        }
        if (result.ageLabel) {
            this.setAttribute('data-stale-label', result.ageLabel);
        } else {
            this.removeAttribute('data-stale-label');
        }
    }

    private computeDirection(newBid?: string): void {
        if (newBid === undefined) return;
        // Reconnect sonrası ilk tick: prevBid kopma anındaki eski değer → sahte büyük
        // zıplama yön/flash gösterir. Bir kez bastır, sadece referansı güncelle (O5).
        const epoch = connectionState.reconnectEpoch;
        if (epoch !== this.lastEpoch) {
            this.lastEpoch = epoch;
            this.prevBid = newBid;
            return;
        }
        if (this.prevBid !== undefined && newBid !== this.prevBid) {
            const a = parseFloat(newBid);
            const b = parseFloat(this.prevBid);
            if (Number.isFinite(a) && Number.isFinite(b) && a !== b) {
                this.direction = a > b ? 'up' : 'down';
                this.triggerFlash(this.direction);
            }
        }
        this.prevBid = newBid;
    }

    /** Kısa süreli arka plan flash'ı — fiyat değişiminde dikkat çeker. */
    private triggerFlash(dir: 'up' | 'down'): void {
        this.flash = dir;
        if (this.flashTimer !== undefined) clearTimeout(this.flashTimer);
        this.flashTimer = window.setTimeout(() => {
            this.flash = '';
        }, 700);
    }

    /** Makas (spread) = satış − alış. Gateway ayrı göndermez; client hesaplar. */
    private spread(): string | null {
        const b = parseFloat(this.tick?.bid ?? '');
        const a = parseFloat(this.tick?.ask ?? '');
        if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
        return formatDelta(a - b);
    }

    render() {
        const t = this.tick;
        const showBid = this.show.includes('bid');
        const showAsk = this.show.includes('ask');
        const showDir = this.show.includes('direction');
        const showSpread = this.show.includes('spread');
        const both = showBid && showAsk;
        const name = getSymbolLabel(this.symbol) ?? this.symbol;
        const arrow = this.direction === 'up' ? '▲' : this.direction === 'down' ? '▼' : '';
        const arrowCls =
            this.direction === 'up' ? 'fp-cell-arrow--up'
            : this.direction === 'down' ? 'fp-cell-arrow--down'
            : '';
        const flashCls = this.flash === 'up' ? 'fp-flash-up' : this.flash === 'down' ? 'fp-flash-down' : '';
        const arrowTpl =
            showDir && arrow ? html`<span class="fp-cell-arrow ${arrowCls}">${arrow}</span>` : '';
        const sp = showSpread ? this.spread() : null;

        const bandCls = this.nameStyle === 'band' ? 'fp-cell--band' : '';

        return html`
            <div class="fp-cell ${bandCls} ${flashCls}">
                <div class="fp-cell-name" title="${name}">${name}</div>

                ${both
                    ? html`
                          <div class="fp-cell-prices">
                              <div class="fp-cell-col">
                                  <span class="fp-cell-lbl">ALIŞ</span>
                                  <span class="fp-cell-val">${formatPrice(t?.bid)}${arrowTpl}</span>
                              </div>
                              <div class="fp-cell-col fp-cell-col--ask">
                                  <span class="fp-cell-lbl">SATIŞ</span>
                                  <span class="fp-cell-val">${formatPrice(t?.ask)}</span>
                              </div>
                          </div>
                      `
                    : html`
                          <div class="fp-cell-single">
                              <span class="fp-cell-lbl">${showAsk && !showBid ? 'SATIŞ' : 'ALIŞ'}</span>
                              <span class="fp-cell-val"
                                  >${formatPrice(showAsk && !showBid ? t?.ask : t?.bid)}${arrowTpl}</span
                              >
                          </div>
                      `}
                ${sp ? html`<div class="fp-cell-spread">Makas ${sp}</div>` : ''}
            </div>
        `;
    }
}
